import React, { useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useEa, useGasUnit } from "@/components/energy-analytics/analytics/EaContext";
import { axisFmt } from "@/components/energy-analytics/analytics/EaConsumptionCharts";
import { useEaData } from "@/components/energy-analytics/analytics/useEaData";
import { EaChartCard, EaField, EaGasUnitToggle, EaNote, EaPageHeader, EaSeg, inputCls } from "@/components/energy-analytics/analytics/EaUi";
import { EaExportButton, EaPointPicker, useSelectedCodes } from "@/components/energy-analytics/analytics/EaControls";
import { COLORS, MONTHS_FULL, MONTHS_IT, fmtEur, fmtQty, fmtSignedPct, missingMsg } from "@/components/energy-analytics/analytics/eaFormat";

const metrics = (g) => [["consumption", "Consumo effettivo", g.f], ["contractual", "Consumo contrattuale", g.f], ["spend", "Spesa fatturata", (v) => fmtEur(v)],
  ["avg_cost", "Costo medio", (v) => fmtQty(g.p(v), 2, g.pu("c€/Smc"))], ["raw_material_price", "Materia prima", (v, u) => fmtQty(g.p(v, u), 2, g.pu(u))]];

function presets(key, y, m, mi) {
  if (key === "mom") return [[y, mi, mi], mi === 1 ? [y - 1, 12, 12] : [y, mi - 1, mi - 1]];
  if (key === "yoy_month") return [[y, mi, mi], [y - 1, mi, mi]];
  return [[y, 1, m], [y - 1, 1, m]];
}

function PeriodInputs({ p, set, years, id }) {
  const sel = (i, v) => set(p.map((x, j) => (j === i ? Number(v) : x)));
  return (
    <div className="flex items-center gap-1">
      <select value={p[0]} onChange={(e) => sel(0, e.target.value)} className={inputCls} data-testid={`ea-gper-${id}-year`}>{years.map((y) => <option key={y} value={y}>{y}</option>)}</select>
      <select value={p[1]} onChange={(e) => sel(1, e.target.value)} className={inputCls} data-testid={`ea-gper-${id}-from`}>{MONTHS_FULL.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select>
      <span className="text-slate-400">→</span>
      <select value={p[2]} onChange={(e) => sel(2, e.target.value)} className={inputCls} data-testid={`ea-gper-${id}-to`}>{MONTHS_FULL.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select>
    </div>
  );
}

function gasWhy(d, k, labs) {
  const nulls = ["a", "b"].filter((s) => d[s][k] == null);
  const side = nulls.find((s) => d[s].missing_points?.length) || nulls[0] || "b";
  const per = d[side], P = `periodo ${side.toUpperCase()} (${labs[side]})`;
  if (per.missing_points?.length) return missingMsg(per.missing_points, P);
  if (k === "contractual") return `Non calcolabile: in database manca il consumo contrattuale del ${P}.`;
  if (k === "raw_material_price") return `Non calcolabile: in database manca il prezzo della materia prima del ${P}.`;
  if ((k === "spend" || k === "avg_cost") && !per.invoiced_months) return `Non calcolabile: in database mancano le fatture del ${P} (o non ci sono mesi fatturati in entrambi i periodi).`;
  return missingMsg([], P);
}

function DeltaCard({ k, label, fmt, d, labs }) {
  const dl = d.delta[k];
  const up = dl.abs != null && dl.abs > 0;
  return (
    <div className="card-elevated p-4" data-testid={`ea-gper-kpi-${k}`} data-pdf="kpi">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500" data-pdf-label>{label}</div>
      <div className="grid grid-cols-2 gap-2 mt-2 font-mono-nums" data-pdf-value>
        <div><div className="text-[10px] text-slate-400">A</div><div className="font-semibold text-slate-900">{fmt(d.a[k], d.a.raw_material_unit || d.b.raw_material_unit)}</div></div>
        <div><div className="text-[10px] text-slate-400">B</div><div className="font-semibold text-slate-600">{fmt(d.b[k], d.a.raw_material_unit || d.b.raw_material_unit)}</div></div>
      </div>
      <div data-pdf-sub>
      <div className={`mt-2 text-sm font-semibold ${dl.abs == null ? "text-slate-400" : up ? "text-red-600" : "text-green-700"}`}>
        {dl.abs == null ? "Δ non calcolabile" : `Δ ${up ? "+" : ""}${fmt(dl.abs, d.a.raw_material_unit || d.b.raw_material_unit)} · ${fmtSignedPct(dl.pct)}`}
      </div>
      {dl.abs == null && <div className="mt-1 text-xs text-slate-500" data-testid={`ea-gper-kpi-${k}-why`}>{gasWhy(d, k, labs)}</div>}
      </div>
    </div>
  );
}

const lab = (p) => `${MONTHS_IT[p[1] - 1]}${p[2] !== p[1] ? `–${MONTHS_IT[p[2] - 1]}` : ""} ${p[0]}`;

export default function EaGasComparePeriods() {
  const { nav } = useEa();
  const codes = useSelectedCodes("GAS");
  const g = useGasUnit();
  const y = Number(nav?.year), m = nav?.availability?.gas_last_actual_month || 12, mi = nav?.availability?.gas_last_invoiced_month || m;
  const years = nav?.years || [y];
  const [preset, setPreset] = useState("ytd");
  const [pa, setPa] = useState(presets("ytd", y, m, mi)[0]);
  const [pb, setPb] = useState(presets("ytd", y, m, mi)[1]);
  const pick = (k) => { setPreset(k); if (k !== "custom") { const [a, b] = presets(k, y, m, mi); setPa(a); setPb(b); } };
  const custom = (set) => (v) => { setPreset("custom"); set(v); };
  const { data: d, loading, error } = useEaData("/ea/gas/compare/periods", { codes, a_year: pa[0], a_from: pa[1], a_to: pa[2], b_year: pb[0], b_from: pb[1], b_to: pb[2] });
  const n = Math.max(pa[2] - pa[1], pb[2] - pb[1]) + 1;
  const chart = d ? Array.from({ length: n }, (_, i) => ({ g: i + 1, label: `${MONTHS_IT[pa[1] - 1 + i] || ""} / ${MONTHS_IT[pb[1] - 1 + i] || ""}`,
    A: g.q(d.a.monthly[i]?.consumption ?? null), B: g.q(d.b.monthly[i]?.consumption ?? null) })) : [];
  return (
    <div className="space-y-6" data-testid="ea-gas-compare-periods">
      <EaPageHeader title="Confronto periodi Gas" subtitle="Periodi mensili a confronto: consumi a consuntivo, spesa e costo medio dei mesi fatturati." units={<EaGasUnitToggle />} right={<EaPointPicker commodity="GAS" />} />
      <div className="card-elevated p-4 flex flex-wrap items-end gap-4" data-testid="ea-gper-controls">
        <EaField label="Confronto rapido"><EaSeg value={preset} onChange={pick} options={[["ytd", "Anno vs anno (stessi mesi)"], ["mom", "Mese vs mese prec."], ["yoy_month", "Mese vs anno prec."], ["custom", "Personalizzato"]]} testid="ea-gper-preset" /></EaField>
        <EaField label="Periodo A"><PeriodInputs p={pa} set={custom(setPa)} years={years} id="a" /></EaField>
        <EaField label="Periodo B"><PeriodInputs p={pb} set={custom(setPb)} years={years} id="b" /></EaField>
      </div>
      {loading ? <div className="text-sm text-slate-400" data-testid="ea-loading">Caricamento…</div> : error ? <EaNote testid="ea-gper-error">{error}</EaNote> : d && <>
        <div className="text-sm text-slate-600" data-testid="ea-gper-labels" data-pdf-filter><b>A</b> {lab(pa)} · <b>B</b> {lab(pb)}</div>
        {["a", "b"].map((s) => !!d[s].missing_points?.length && <EaNote key={s} testid={`ea-gper-${s}-missing`}>{missingMsg(d[s].missing_points, `periodo ${s.toUpperCase()} (${lab(s === "a" ? pa : pb)})`)}</EaNote>)}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {metrics(g).map(([k, l, f]) => <DeltaCard key={k} k={k} label={l} fmt={f} d={d} labs={{ a: lab(pa), b: lab(pb) }} />)}
        </div>
        <EaNote>Consumi: A {d.a.actual_months} mesi a consuntivo · B {d.b.actual_months}. Spesa e costo medio confrontati solo sui mesi fatturati in entrambi i periodi ({d.a.invoiced_months}). Δ rosso = aumento.</EaNote>
        <EaChartCard title="Consumo mensile A vs B" subtitle="Mesi allineati per posizione" testid="ea-gper-card"
          right={<EaExportButton filename="gas_confronto_periodi" commodity="GAS" period={`A ${lab(pa)} vs B ${lab(pb)}`} testid="ea-gper-export"
            columns={[{ key: "label", label: "Mesi A / B" }, { key: "A", label: `A ${g.u}` }, { key: "B", label: `B ${g.u}` }]} rows={chart} />}>
          <div className="h-72" data-testid="ea-gper-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} width={80} tickFormatter={axisFmt} />
                <Tooltip formatter={(v, nm) => [fmtQty(v, g.d, g.u), nm === "A" ? `A ${lab(pa)}` : `B ${lab(pb)}`]} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="A" fill={COLORS.gas} radius={[3, 3, 0, 0]} />
                <Bar dataKey="B" fill="#CBD5E1" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </EaChartCard>
      </>}
    </div>
  );
}
